import React, { useRef, useState, useEffect } from "react";
import DateDetailModal from "./DateDetailModal";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import { useDispatch, useSelector } from "react-redux";
import "./css/customCalendar.css";
import { useNavigate } from "react-router-dom";
import userStore from "../../zustand/userStore";
import {
  loadCalendarItemsFromLocalStorage,
  fetchHolidaysThunk,
  fetchSessionsThunk,
  fetchCompetitionsThunk,
  fetchExamsThunk,
  fetchPtmsThunk,
  fetchCelebrationsThunk,
  fetchAwarenessThunk,
} from "./redux/academicCalendarSlice";
// In CustomCalendar.js (after imports, before component declaration)
import { fetchOrganiserEvents } from "../EventOrganiser/redux/eventOrganiserThunks";
import { setAppdAiUser } from "../../utils/appdAiHelpers";
console.log("CustomCalendar component file loaded and parsed!");
// --- Holiday Range Expansion Helpers ---
const parseDateFlexible = (d) => {
  if (!d) return moment.invalid();

  const strict = moment(d, "DD-MM-YYYY", true);
  if (strict.isValid()) return strict;

  const auto = moment(d);
  return auto.isValid() ? auto : moment.invalid();
};


const expandMultiDayEvent = (item) => {
  // Do NOT expand session start or end
  if (item.type === "sessionStart" || item.type === "sessionEnd") {
    return [{
      ...item,
      date: parseDateFlexible(item.date).format("DD-MM-YYYY"),
      start_date: item.date,
      end_date: item.date
    }];
  }

  const start = parseDateFlexible(item.start_date || item.date);
  const end = parseDateFlexible(item.end_date || item.date);

  if (!start.isValid() || !end.isValid()) {
    return [{
      ...item,
      date: start.isValid() ? start.format("DD-MM-YYYY") : item.date,
      start_date: item.start_date || item.date,
      end_date: item.end_date || item.date
    }];
  }

  const res = [];
  let cur = start.clone();

  while (cur.isSameOrBefore(end, "day")) {
    res.push({
      ...item,
      date: cur.format("DD-MM-YYYY"),
      start_date: start.format("DD-MM-YYYY"),   // <-- IMPORTANT
      end_date: end.format("DD-MM-YYYY")        // <-- IMPORTANT
    });
    cur.add(1, "day");
  }

  return res;
};
const isEventStart = (item) => {
  if (!item.start_date || !item.end_date) return false;
  if (item.start_date === item.end_date) return false; // <-- single-day event
  if (item.type === "sessionStart" || item.type === "sessionEnd") return false;

  return item.date === item.start_date;
};

const isEventEnd = (item) => {
  if (!item.start_date || !item.end_date) return false;
  if (item.start_date === item.end_date) return false; // <-- single-day event
  if (item.type === "sessionStart" || item.type === "sessionEnd") return false;

  return item.date === item.end_date;
};






const CustomCalendar = ({ initialView = "month", onSessionInfoUpdate }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const academicCalendarData = useSelector(
    (state) => state.academicCalendar.academicData
  );
  const sessions = useSelector(
    (state) => state.academicCalendar.sessions
  );
  console.log(academicCalendarData);
  const organiserEvents = useSelector((state) => state.eventOrganiser.Events);
  console.log(organiserEvents, "Organiser Events in CustomCalendar");
  const expandedOrganiserEvents = organiserEvents.flatMap((e) =>
    expandMultiDayEvent({
      ...e,
      type: "organiserEvent",
      date: moment(e.date, "DD-MM-YYYY").isValid()
        ? moment(e.date, "DD-MM-YYYY").format("DD-MM-YYYY")
        : moment(e.start_time).format("DD-MM-YYYY"),
    })
  );

  const expandedAcademic = academicCalendarData.flatMap(expandMultiDayEvent);



  const [currentDate, setCurrentDate] = useState(moment());
  const [view, setView] = useState(initialView);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDateForModal, setSelectedDateForModal] = useState(null);
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const hasAutoSelectedSessionRef = useRef(false);

  const getSessionFocusDate = (session) => {
    if (!session) return moment();

    const today = moment();
    const sessionStart = moment(session.start_date);
    const sessionEnd = moment(session.end_date);

    if (!sessionStart.isValid() || !sessionEnd.isValid()) {
      return today;
    }

    if (today.isBetween(sessionStart, sessionEnd, "day", "[]")) {
      return today;
    }

    return sessionStart;
  };

  const allEvents = [
    ...expandedAcademic,
    ...expandedOrganiserEvents,
  ];

  const filteredEvents = selectedSession
    ? allEvents.filter((item) => {
      const itemDate = moment(
        item.start_date || item.date,
        "DD-MM-YYYY"
      );

      const sessionStart = moment(selectedSession.start_date);

      const sessionEnd = moment(selectedSession.end_date);

      return itemDate.isBetween(
        sessionStart,
        sessionEnd,
        "day",
        "[]"
      );
    })
    : allEvents;
  const dateInputRef = useRef(null);
  const printRef = useRef();
  const userData = userStore((state) => state.user);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "Academic Calendar",
  });

  // useEffect(() => {
  //   if (userData) setAppdAiUser(userData, "demo");
  // }, [])

  useEffect(() => {
    console.log("Component Name: userData loaded:", userData); // Replace 'Component Name'
    console.log("Component Name: userData.id before dispatch:", userData.id); // Replace 'Component Name'
    dispatch(loadCalendarItemsFromLocalStorage());
    dispatch(fetchHolidaysThunk({ user_id: userData.id })); // Add user_id
    dispatch(fetchSessionsThunk());
    dispatch(fetchCompetitionsThunk({ user_id: userData.id })); // Add user_id
    dispatch(fetchExamsThunk({ user_id: userData.id })); // Add user_id
    dispatch(fetchCelebrationsThunk({ user_id: userData.id })); // Add user_id
    dispatch(fetchAwarenessThunk({ user_id: userData.id })); // Add user_id
    dispatch(fetchOrganiserEvents({ userId: userData.id })); // Fetch organiser events
  }, [dispatch, userData.id]); // Add userData.id to dependency array

  useEffect(() => {
    if (sessions?.length > 0 && !selectedSession) {
      const activeSession = sessions.find(
        (session) => session.status === "active"
      );

      if (activeSession) {
        hasAutoSelectedSessionRef.current = true;
        setSelectedSession(activeSession);
      }
    }
  }, [sessions]);

  useEffect(() => {
    if (!selectedSession) return;

    if (hasAutoSelectedSessionRef.current) {
      hasAutoSelectedSessionRef.current = false;
      return;
    }

    setCurrentDate(getSessionFocusDate(selectedSession));
  }, [selectedSession]);

  // Effect to update session info in parent component
  useEffect(() => {
    const sessionStartItem = academicCalendarData.find(
      (item) => item.type === "sessionStart"
    );
    const sessionEndItem = academicCalendarData.find(
      (item) => item.type === "sessionEnd"
    );

    const sessionStart = sessionStartItem
      ? moment(sessionStartItem.date, "DD-MM-YYYY").format("YYYY-MM-DD")
      : "Not Set";
    const sessionEnd = sessionEndItem
      ? moment(sessionEndItem.date, "DD-MM-YYYY").format("YYYY-MM-DD")
      : "Not Set";
    const workingDays = getTotalWorkingDays();

    if (onSessionInfoUpdate) {
      onSessionInfoUpdate({
        sessionStart,
        sessionEnd,
        workingDays,
      });
    }
  }, [academicCalendarData, onSessionInfoUpdate]);

  const handleDayClick = (date, dayEvents) => {
    // Only open modal on mobile/tablet
    // if (window.innerWidth <= 1024) {
    setSelectedDateForModal(date);
    setSelectedDateEvents(dayEvents);
    setModalOpen(true);
    document.body.classList.add('modal-open');
    // }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedDateForModal(null);
    setSelectedDateEvents([]);
    document.body.classList.remove('modal-open');
  };
  const getTotalWorkingDays = () => {
    let sessionStartItem = academicCalendarData.find(
      (item) => item.type === "sessionStart"
    );
    let sessionEndItem = academicCalendarData.find(
      (item) => item.type === "sessionEnd"
    );

    let sessionStart = sessionStartItem
      ? moment(sessionStartItem.date, "DD-MM-YYYY").startOf("day")
      : null;
    let sessionEnd = sessionEndItem
      ? moment(sessionEndItem.date, "DD-MM-YYYY").endOf("day")
      : null;

    if (!sessionStart || !sessionEnd) {
      return "N/A (Session start/end not defined)";
    }

    let workingDays = 0;
    let current = sessionStart.clone();

    const allEvents = [...academicCalendarData];

    while (current.isSameOrBefore(sessionEnd, "day")) {
      const dayOfWeek = current.day();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      const isHoliday = allEvents.some(
        (item) =>
          item.type === "holiday" &&
          moment(item.date || item.start_date, "DD-MM-YYYY").isSame(current, "day")
      );

      if (!isWeekend && !isHoliday) {
        workingDays++;
      }
      current.add(1, "day");
    }
    return workingDays;
  };

  const getBackgroundColorForType = (type) => {
    switch (type) {
      case "sessionStart":
        return "#D8BFD8";
      case "celebration":
        return "#4169E1";
      case "awarenessDay":
        return "#FFD700";
      case "event":
        return "#4169E1";
      case "organiserEvent":
        return "#FF8C00";
      case "holiday":
        return "#FFFF00";
      case "competition":
        return "#FFC0CB";
      case "exam":
        return "#90EE90"; // Changed to match desired UI
      case "ptm":
        return "#DDA0DD"; // Changed to match desired UI
      case "sessionEnd":
        return "#8A2BE2";
      default:
        return "#eee";
    }
  };

  const getAllAcademicAndUserEventsForDate = (date) => {
    const dateStr = date.format("DD-MM-YYYY");
    const combinedEvents = filteredEvents.filter(
      (item) => item.date === dateStr
    );

    return combinedEvents;
  };

  const handlePrev = () => {
    setCurrentDate((prev) =>
      view === "month"
        ? prev.clone().subtract(1, "month")
        : view === "week"
          ? prev.clone().subtract(1, "week")
          : prev.clone().subtract(1, "day")
    );
  };

  const handleNext = () => {
    setCurrentDate((prev) =>
      view === "month"
        ? prev.clone().add(1, "month")
        : view === "week"
          ? prev.clone().add(1, "week")
          : prev.clone().add(1, "day")
    );
  };

  const handleMoreClick = (date) => {
    setCurrentDate(moment(date));
    setView("day");
  };

  const RenderMonthView = () => {
    const firstDayOfMonth = currentDate.clone().startOf("month");
    const daysInMonth = currentDate.daysInMonth();
    const startDay = firstDayOfMonth.day();

    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    return (
      <div className="table-responsive">
        <div className="calendar-grid">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} className="calendar-day-header">
              {day}
            </div>
          ))}

          {Array.from({ length: startDay }).map((_, index) => (
            <div key={`empty-${index}`} className="calendar-day empty"></div>
          ))}

          {days.map((day) => {
            const date = currentDate.clone().date(day);
            const allCalendarItems = getAllAcademicAndUserEventsForDate(date);

            const typesOnDay = [...new Set(allCalendarItems.map((item) => item.type))];

            let backgroundStyle = {};

            if (date.day() === 0) {
              backgroundStyle = {
                backgroundColor: "#fd686dff",
                borderRadius: "12px",
                boxShadow: "0 0 8px rgba(255, 77, 79, 0.4)",
                color: "white"
              };
            } else if (typesOnDay.length === 1) {
              backgroundStyle.background = getBackgroundColorForType(typesOnDay[0]);
            } else if (typesOnDay.length === 2) {
              // backgroundStyle.background = `linear-gradient(to bottom, ${getBackgroundColorForType(typesOnDay[0])} 50%, ${getBackgroundColorForType(typesOnDay[1])} 50%)`;
            } else if (typesOnDay.length >= 3) {
              const gradientParts = typesOnDay.slice(0, 3).map((type, index) => {
                const percent = Math.floor(100 / typesOnDay.length);
                return `${getBackgroundColorForType(type)} ${index * percent}% ${(index + 1) * percent}%`;
              });
              // backgroundStyle.background = `linear-gradient(to bottom, ${gradientParts.join(', ')})`;
            }

            const isSessionStartDate = academicCalendarData.some(
              (item) =>
                item.type === "sessionStart" &&
                moment(item.date, "DD-MM-YYYY").isSame(date, "day")
            );
            const isSessionEndDate = academicCalendarData.some(
              (item) =>
                item.type === "sessionEnd" &&
                moment(item.date, "DD-MM-YYYY").isSame(date, "day")
            );

            return (
              <div
                key={day}
                className="calendar-day"
                style={{ ...backgroundStyle, cursor: 'pointer' }}
                onClick={() => handleDayClick(date, allCalendarItems)}
              >                {/* style={{
                      backgroundColor:
                        getBackgroundColorForType("sessionStart"),
                    }} */}
                <div className="day-number">{day}</div>
                {isSessionStartDate && (
                  <div
                    className="academic-event"
                    style={{
                      // backgroundColor:getBackgroundColorForType("sessionStart"),
                    }}
                    title="Session Start"
                  >
                    Session Start
                  </div>
                )}
                {isSessionEndDate && (
                  <div
                    className="academic-event"
                    style={{
                      // backgroundColor: getBackgroundColorForType("sessionEnd"),
                    }}
                    title="Session End"
                  >
                    Session End
                  </div>
                )}
                <div>
                  {allCalendarItems
                    .filter(
                      (item) =>
                        item.type !== "sessionStart" && item.type !== "sessionEnd"
                    )
                    .map((item, index) => (
                      <div
                        key={`academic-${item.type}-${index}`}
                        className="academic-event mb-2 cursor-pointer"
                        style={{
                          backgroundColor: getBackgroundColorForType(item.type),
                          fontSize: "14px", borderRadius: "8px", padding: "0 3px",
                          color: item.type === "holiday" || item.type === "awarenessDay" ? "black" : "white", // Holidays and Awareness Days have black text
                        }}
                        title={
                          (item.name || item.details) +
                          (item.start_time
                            ? ` (${item.start_time}-${item.end_time})`
                            : "")
                        }
                        onClick={(e) => { // Add onClick handler
                          //  GO TO organiser events page
                          if (item.type === "organiserEvent") {
                            navigate("/showEvents");
                            return;
                          }
                          if (item.type === "awarenessDay") {
                            e.stopPropagation(); // Prevent the day cell's click handler from firing if it existed
                            navigate(`/awareness-details/${item.awareness_id || moment(item.date).format("DD-MM-YYYY")}`);
                          } else if (item.type === "celebration") {
                            navigate(`/celebration-details/${item.celebration_id || item.date}`)
                          }
                        }}
                      >
                        {
                          isEventStart(item)
                            ? `${item.name || item.details} - Start`
                            : isEventEnd(item)
                              ? `${item.name || item.details} - End`
                              : item.name || item.details
                        }

                      </div>
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTimeBlocks = (dayMoment) => {
    const allCalendarItems = getAllAcademicAndUserEventsForDate(dayMoment);

    const allDayOnlyItems = allCalendarItems.filter(item =>
      item.type === 'holiday' ||
      (item.type === 'sessionStart' && !item.start_time && !item.end_time) ||
      (item.type === 'sessionEnd' && !item.start_time && !item.end_time) ||
      ((item.type === 'event' || item.type === 'celebration' || item.type === 'competition' || item.type === 'exam' || item.type === 'ptm' || item.type === 'awarenessDay') && (!item.start_time || !item.end_time))
    );

    let allTimedItems = allCalendarItems.filter(
      (a) => a.start_time && a.end_time && a.type !== "sessionStart" && a.type !== "sessionEnd"
    ).map((a) => ({ ...a, isAcademic: true }))
      .sort((a, b) =>
        moment(a.start_time, "HH:mm").diff(moment(b.start_time, "HH:mm"))
      );

    const isSessionStartDate = academicCalendarData.some(
      (item) =>
        item.type === "sessionStart" &&
        moment(item.date, "DD-MM-YYYY").isSame(dayMoment, "day")
    );
    const isSessionEndDate = academicCalendarData.some(
      (item) =>
        item.type === "sessionEnd" &&
        moment(item.date, "DD-MM-YYYY").isSame(dayMoment, "day")
    );

    if (isSessionStartDate && !allDayOnlyItems.some(item => item.type === "sessionStart")) {
      allTimedItems.unshift({
        name: "Session Start",
        type: "sessionStart",
        start_time: "00:00",
        end_time: "00:00",
        isAcademic: true,
        virtual: true,
      });
    }
    if (isSessionEndDate && !allDayOnlyItems.some(item => item.type === "sessionEnd")) {
      allTimedItems.unshift({
        name: "Session End",
        type: "sessionEnd",
        start_time: "00:00",
        end_time: "00:00",
        isAcademic: true,
        virtual: true,
      });
    }

    const blocks = [];

    const renderedAllDayItems = allDayOnlyItems.map((item, index) => (
      <div
        key={`all-day-${item.type}-${index}`}
        className="academic-event all-day-event"
        style={{
          backgroundColor: getBackgroundColorForType(item.type),
          padding: "2px 4px",
          fontSize: "12px",
          borderRadius: "4px",
          marginBottom: "2px",
          marginLeft: "10px",
          marginRight: "10px",
          marginTop: "10px",
          fontWeight: "bold",
          textAlign: "center",
          color: item.type === "holiday" || item.type === "awarenessDay" ? "black" : "white",
        }}
        title={item.name || item.details}
      >
        {item.name || item.details}
      </div>
    ));

    let currentTime = moment("07:00", "HH:mm");
    const endTime = moment("23:00", "HH:mm");

    for (let item of allTimedItems) {
      const itemStart = moment(item.start_time, "HH:mm");
      const itemEnd = moment(item.end_time, "HH:mm");

      if (itemStart.isAfter(currentTime)) {
        blocks.push({
          type: "free",
          start: currentTime.clone(),
          end: itemStart.clone(),
        });
      }

      blocks.push({
        type: "occupied",
        start: itemStart.clone(),
        end: itemEnd.clone(),
        academicItem: item,
        isAcademic: true,
        itemType: item.type,
      });

      currentTime = itemEnd.clone();
    }

    if (currentTime.isBefore(endTime)) {
      blocks.push({
        type: "free",
        start: currentTime,
        end: endTime,
      });
    }

    const renderedTimedBlocks = blocks.map((block, index) => {
      const durationInMinutes = block.end.diff(block.start, "minutes");
      const height = (durationInMinutes / 60) * 40;

      const backgroundColor =
        block.type === "occupied"
          ? getBackgroundColorForType(block.itemType)
          : "rgba(144, 238, 144, 0.71)";

      const textColor = block.type === "occupied" && (block.itemType === "holiday" || block.itemType === "awarenessDay") ? "black" : "white";

      return (
        <div
          key={`timed-block-${index}`}
          onClick={() => {
            if (block.type === "occupied") {
              console.log("Academic Item Data:", block.academicItem);
            }
          }}
          style={{
            height: `${height}px`,
            backgroundColor: backgroundColor,
            borderBottom: "1px solid #ccc",
            padding: "2px 4px",
            fontSize: "12px",
            boxSizing: "border-box",
            borderRadius: "20px",
            marginBottom: "2px",
            marginLeft: "10px",
            marginRight: "10px",
            marginTop: "10px",
            color: textColor,
          }}
        >
          {/* <strong style={{ marginLeft: "10px" }}>
            {block.start.format("HH:mm")} - {block.end.format("HH:mm")}
          </strong> */}
          {block.type === "occupied" && (
            <div
              style={{
                fontWeight: "bold",
                fontSize: "12px",
                marginLeft: "10px",
              }}
            >
              {block.academicItem.name || block.academicItem.details}
            </div>
          )}
        </div>
      );
    });
    return [...renderedAllDayItems, ...renderedTimedBlocks];
  };

  const renderWeekView = () => {
    const startOfWeek = currentDate.clone().startOf("week");
    const weekDays = Array.from({ length: 7 }).map((_, index) =>
      startOfWeek.clone().add(index, "days")
    );

    return (
      <div className="table-responsive">
        <div
          className="calendar-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: "10px",
          }}
        >
          {weekDays.map((day) => (
            <div
              key={day.format("YYYY-MM-DD")}
              className="calendar-day-header"
              style={{
                padding: "6px",
                borderBottom: "1px solid #ccc",
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "14px",
                marginRight: "6px",
                marginLeft: "6px",
                marginBottom: "4px",
                borderRadius: "25px",
              }}
            >
              {day.format("ddd")} <br />
              <span style={{ fontWeight: "normal", fontSize: "12px" }}>
                {day.format("D MMM")}
              </span>
            </div>
          ))}

          {weekDays.map((day) => {
            return (
              <div
                key={day.format("YYYY-MM-DD") + "-events"}
                style={{
                  border: "1px solid #ddd", // Changed to match desired UI
                  height: "100%",
                  borderRadius: "15px", // Changed to match desired UI
                  marginRight: "6px",
                  marginLeft: "6px",
                  minHeight: "80px", // Ensure minimum height for week view days
                }}
              >
                {renderTimeBlocks(day)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    return (
      <div
        className="calendar-day-view"
        style={{ display: "flex", flexDirection: "column", gap: "10px" }}
      >
        {renderTimeBlocks(currentDate)}
      </div>
    );
  };

  const handleDateChange = (e) => {
    if (e.target.value) setCurrentDate(moment(e.target.value));
  };

  return (
    <div className="calendar" ref={printRef}>
      <div style={{
        marginBottom: "10px",
        width: "200px",
      }}>
        <select
          value={selectedSession?.id || ""}
          onChange={(e) => {
            const session = sessions.find(
              (s) => String(s.id) === e.target.value
            );

            setSelectedSession(session);
          }}
        >
          {sessions.map((session) => (
            <option key={session.id} value={session.id}>
              {moment(session.start_date).format("YYYY")} -
              {moment(session.end_date).format("YYYY")}
              (
              {session.status === "lapsed"
                ? "Concluded"
                : session.status === "active"
                  ? "Active"
                  : session.status}
              )
            </option>
          ))}
        </select>
      </div>
      <div className="calendar-header">

        {/* Left: Year */}
        <div className="bold text-14 calendar-year">
          {currentDate.format("YYYY")}
        </div>

        {/* Center: Month/Day Navigation */}
        <div className="d-flex gap-2 bold text-14 calendar-navigation">
          <div onClick={handlePrev} className="custom-symbol">
            <i className="fa fa-angle-left text-18" aria-hidden="true"></i>
          </div>
          <div className="current-date-display">
            {(view === "month" || view === "week") &&
              currentDate.format("MMMM")}
            {view === "day" && (
              <span
                onClick={() => {
                  dateInputRef.current.showPicker();
                }}
                style={{
                  cursor: "pointer",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                {currentDate.format("DD MMMM")}
              </span>
            )}
          </div>
          <div onClick={handleNext} className="custom-symbol">
            <i className="fa fa-angle-right text-18" aria-hidden="true"></i>
          </div>
          <input
            className="visually-hidden"
            type="date"
            ref={dateInputRef}
            onChange={handleDateChange}
          />
        </div>


        {/* Right: View Switcher */}
        <div className="d-flex gap-1 view-switcher-buttons">
          <button
            onClick={handlePrint}
            style={{
              border: "1px solid #ccc",
              borderRadius: "6px",
              padding: "4px 10px",
              background: "#fff",
              cursor: "pointer",
              marginRight: "10px"
            }}
          >
            Print
          </button>
          <div
            onClick={() => setView("month")}
            className={`${view === "month" && "bold"} text-14 border rounded px-1`}
            style={{ cursor: "pointer" }}
          >
            Month
          </div>
          <div
            onClick={() => setView("week")}
            className={`${view === "week" && "bold"} text-14 border rounded px-1`}
            style={{ cursor: "pointer" }}
          >
            Week
          </div>
          <div
            onClick={() => setView("day")}
            className={`${view === "day" && "bold"} text-14 border rounded px-1`}
            style={{ cursor: "pointer" }}
          >
            Day
          </div>
        </div>
      </div>
      <div className="mt-2">
        {view === "month" && <RenderMonthView />}
        {view === "week" && renderWeekView()}
        {view === "day" && renderDayView()}
      </div>
      <DateDetailModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        selectedDate={selectedDateForModal}
        events={selectedDateEvents}
      />
    </div>
  );
};

export default CustomCalendar;
